import React from 'react';
import Table, { TableColumn } from '../../components/common/Table';
import { ActivityDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { getBadgeColors } from '../../utils/displayUtils';
import { renderCaliberBadge } from '../../utils/firearmDisplayUtils';
import FileIcons from '../common/FileIcons';
import Badge from '../common/Badge';
import { getRoles } from '../../utils/tokenUtils';
import createActivityActions from '../actionMenus/ActivityActions';
import FeedbackBubble from '../common/FeedbackBubble';
import { useActivityApi } from '../../hooks/useActivityApi';
import { ActivityTypeConstants } from '../../constants/ActivityTypeConstants';
import { ActivityStatusConstants } from '../../constants/ActivityStatusConstants';
import MemberNoteIcons from '../common/MemberNoteIcons';
import ReviewPanel from '../reviewPanels/ReviewPanel';
import { FirearmCaliberConstants } from '../../constants/FirearmCaliberConstants';
import { FirearmMakeConstants } from '../../constants/FirearmMakeConstants';

interface ActivityTableProps {
  data: ActivityDTO[];
  handleViewMemberDetails: (row: ActivityDTO) => void;
  handleEdit: (row: ActivityDTO) => void;
  handleDelete: (row: ActivityDTO) => Promise<void>;
  handleSendEmail: (row: ActivityDTO) => void;
  handleViewAllEmails: (row: ActivityDTO) => void;
  handleViewRecordEmails: (row: ActivityDTO) => void;
  handleRestore: (row: ActivityDTO) => Promise<void>;
  handleApprove: (row: ActivityDTO) => void;
  handleChangeStatus: (row: ActivityDTO) => void;
  handleImpersonateMemberLogin: (row: ActivityDTO) => void;
  handleAddNote: (row: ActivityDTO) => void;
  handleViewNoteHistory: (row: ActivityDTO) => void;
  handleMemberClick?: (memberId: string) => void;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
  sortBy?: string;
  sortDescending?: boolean;
  onSort?: (sortKey: string) => void;
  selectedIds?: Set<string>;
  onSelectRow?: (id: string, checked: boolean) => void;
  onSelectAll?: (checked: boolean) => void;
}

const ActivityTable: React.FC<ActivityTableProps> = ({
  data,
  handleViewMemberDetails,
  handleEdit,
  handleDelete,
  handleSendEmail,
  handleViewAllEmails,
  handleViewRecordEmails,
  handleRestore,
  handleApprove,
  handleChangeStatus,
  handleImpersonateMemberLogin,
  handleAddNote,
  handleViewNoteHistory,
  handleMemberClick,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
  sortBy,
  sortDescending,
  onSort,
  selectedIds = new Set(),
  onSelectRow,
  onSelectAll,
}) => {
  const roles = getRoles();
  const isAdmin = roles?.includes('Admin');
  const { getFeedback } = useActivityApi();

  const allCurrentPageIds = data.map(row => row.id).filter((id): id is string => !!id);
  const allSelected = allCurrentPageIds.length > 0 && allCurrentPageIds.every(id => selectedIds.has(id));
  const someSelected = allCurrentPageIds.some(id => selectedIds.has(id)) && !allSelected;

  // Check if status is locked (Approved, Approved with Feedback, or Declined)
  const isStatusLocked = (row: ActivityDTO): boolean => {
    const lockedStatuses = [
      ActivityStatusConstants.APPROVED.toLowerCase(),
      ActivityStatusConstants.APPROVED_WITH_FEEDBACK.toLowerCase(),
      ActivityStatusConstants.OUTSTANDING.toLowerCase(),
      ActivityStatusConstants.DECLINED.toLowerCase(),
    ];
    return lockedStatuses.includes(row.activityStatusId?.toLowerCase() || '');
  };

  const columns: TableColumn[] = [
    ...(onSelectRow && onSelectAll
      ? [
        {
          label: (
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = someSelected;
              }}
              onChange={(e) => onSelectAll?.(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
            />
          ),
          key: 'checkbox',
          render: (row: ActivityDTO) => (
            <input
              type="checkbox"
              checked={selectedIds.has(row.id ?? '')}
              onChange={(e) => onSelectRow?.(row.id ?? '', e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              className="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
            />
          ),
        },
      ]
      : []),
    ...(isAdmin
      ? [
        {
          label: 'Member',
          key: 'member',
          sortable: true,
          sortKey: 'FirstName',
          showInReview: true,
          render: (row: ActivityDTO) => (
            <div
              onClick={() => handleMemberClick?.(row.member?.id ?? '')}
              className="flex flex-col gap-1 cursor-pointer"
            >
              <span>
                {row.member?.firstName} {row.member?.lastName}
              </span>
              <span>{row.member?.idPassportNumber ?? ''}</span>
              <MemberNoteIcons member={row.member} tableContext="activities" recordId={row.id} />
            </div>
          ),
        },
        // {
        //   label: 'Cellphone',
        //   key: 'cellphone',
        //   render: (row: ActivityDTO) => <span>{row.member?.phone}</span>,
        // },
        // {
        //   label: 'Email',
        //   key: 'email',
        //   render: (row: ActivityDTO) => <span>{row.member?.email}</span>,
        // },
        {
          label: 'Membership',
          key: 'membershipType',
          sortable: true,
          sortKey: 'MemberTypeTitle',
          showInReview: true,
          render: (row: ActivityDTO) => <span>{row.member?.memberType?.title}</span>,
        },
        {
          label: 'FCA',
          key: 'memberType',
          sortable: true,
          sortKey: 'MemberCategoryTitle',
          showInReview: true,
          render: (row: ActivityDTO) => <span>{row.member?.memberCategory?.titleShort}</span>,
        },
      ]
      : []),

    {
      label: isAdmin ? 'Date of Activity' : 'Date',
      key: 'activityDate',
      sortable: true,
      sortKey: 'ActivityDate',
      showInReview: true,
      render: (row: ActivityDTO) => {
        return (
          <>
            {row.activityDate
              ? convertToUserTime(row.activityDate?.toString() ?? '', true)
              : row.dateYear}
          </>
        );
      },
    },

    ...(isAdmin
      ?
      [
        {
          label: 'Location',
          key: 'location',
          sortable: true,
          sortKey: 'Location',
          showInReview: true,
          render: (row: ActivityDTO) => <span>{row.location}</span>,
        },
        {
          label: 'Town',
          key: 'town',
          render: (row: ActivityDTO) => <span>{row.town ?? '-'}</span>,
        }
      ]
      :
      [
        {
          label: 'Location',
          key: 'location',
          render: (row: ActivityDTO) => <span>{row.activityTypeId === ActivityTypeConstants.NON_ACTIVE_REPORT ? row.country?.title : [row.country?.title, row.province?.title, row.town, row.location].filter(Boolean).join(', ')}</span>,
        },
      ]),

    {
      label: 'Type',
      key: 'activityTypeId',
      sortable: true,
      sortKey: 'ActivityTypeTitle',
      showInReview: true,
      render: (row: ActivityDTO) => <span>{row.activityType?.title}</span>,
    },

    {
      label: isAdmin ? 'Category' : 'Activity Type',
      key: 'activityCategoryId',
      sortable: true,
      sortKey: 'ActivityCategoryTitle',
      showInReview: true,
      render: (row: ActivityDTO) => <span>{row.activityCategory?.title ?? row.nonActiveType?.title}</span>,
    },




    ...(isAdmin
      ? [
        {
          label: 'Calibre',
          key: 'calibre',
          sortable: true,
          sortKey: 'CaliberTitle',
          showInReview: true,
          render: (row: ActivityDTO) => renderCaliberBadge(
            row.memberFirearm?.firearmCaliberId === FirearmCaliberConstants.NOT_ON_LIST ? row.memberFirearm?.caliberOther : row.memberFirearm?.caliber?.title,
            row.memberFirearm?.firearmSpecificId
          ) || <span>-</span>,
        },
      ]
      : []),

    ...(!isAdmin
      ? [
        {
          label: 'Firearm',
          key: 'memberFirearm',
          render: (row: ActivityDTO) => {
            const makeTitle = row.memberFirearm?.firearmMakeId === FirearmMakeConstants.NOT_ON_LIST ? row.memberFirearm?.firearmMakeOther : row.memberFirearm?.firearmMake?.title ?? '';
            const caliberTitle = row.memberFirearm?.firearmCaliberId === FirearmCaliberConstants.NOT_ON_LIST ? row.memberFirearm?.caliberOther : row.memberFirearm?.caliber?.title ?? '';
            return (
              <span>{makeTitle} - {caliberTitle}</span>
            );
          },
        },
      ]
      : []),

    {
      label: 'Documents',
      key: 'documents',
      render: (row: ActivityDTO) => (
        <FileIcons
          files={[
            {
              id: row.confirmationFileId,
              label: 'Activity File',
            },
            {
              id: row.additionalConfirmationFile1Id,
              label: 'Activity File 2',
            },
            {
              id: row.additionalConfirmationFile2Id,
              label: 'Activity File 3',
            },
          ]}
          renderSidePanel={isAdmin ? () => <ReviewPanel row={row} columns={columns} /> : undefined}
        />
      ),
    },

    ...(isAdmin
      ? [
        {
          label: 'FB',
          key: 'feedback',
          render: (row: ActivityDTO) => (
            <FeedbackBubble
              hasFeedback={!!row.feedbackFileId}
              fetchFeedback={() => getFeedback(row.id!)}
            />
          ),
        },
      ]
      : []),

    {
      label: 'Status',
      key: 'activityStatus',
      sortable: true,
      sortKey: 'ActivityStatusTitle',
      showInReview: true,
      render: (row: ActivityDTO) => {
        if (row.isDeleted) {
          return <Badge label="Deleted" variant="error" />;
        }
        const base = row.activityStatus?.colourGrading?.colour ?? '#ffffffff';
        const { bg, text } = getBadgeColors(base, 0.18, 0.4);
        return base ? (
          <Badge
            label={row.activityStatus?.title || 'Unknown'}
            variant="custom"
            customBgColor={bg}
            customTextColor={text}
          />
        ) : (
          <span>{row.activityStatus?.title || 'Unknown'}</span>
        );
      },
    },

    ...(isAdmin
      ? [
        {
          label: 'Updated',
          key: 'updatedUtc',
          sortable: true,
          sortKey: 'UpdatedUtc',
          format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
        },
        {
          label: 'Created',
          key: 'createdUtc',
          sortable: true,
          sortKey: 'CreatedUtc',
          format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
        },
      ] :
      []),
    ...(!isAdmin
      ? [
        {
          label: 'Applied',
          key: 'createdUtc',
          sortable: true,
          sortKey: 'CreatedUtc',
          format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
        },
      ]
      : []),
  ];

  return (
    <>
      <Table
        columns={columns}
        data={data}
        actions={{
          ...(!isAdmin && {
            extra: (row) => {
              // For non-admin users, show edit action conditionally
              if (isStatusLocked(row)) {
                return [];
              }
              return [
                {
                  label: 'Edit Activity',
                  onClick: () => handleEdit(row),
                },
              ];
            },
          }),
          ...(isAdmin && {
            onEdit: handleEdit,
            extra: (row) =>
              createActivityActions({
                onViewMemberDetails: handleViewMemberDetails,
                onView: handleEdit,
                onDelete: handleDelete,
                onSendEmail: handleSendEmail,
                onViewAllEmails: handleViewAllEmails,
                onViewRecordEmails: handleViewRecordEmails,
                onRestore: handleRestore,
                onApprove: handleApprove,
                onChangeStatus: handleChangeStatus,
                onImpersonateMemberLogin: handleImpersonateMemberLogin,
                onAddNote: handleAddNote,
                onViewNoteHistory: handleViewNoteHistory,
              })(row),
          }),
        }}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        isLoading={isLoading}
        sortBy={sortBy}
        sortDescending={sortDescending}
        onSort={onSort}
      />
    </>
  );
};

export default ActivityTable;
